import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import CustomSelect from "../../components/customSelect";
import { Input } from "../../components/inputs/Input";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_LIST_EVENTS_BY_ORGANIZATION } from "../../queries";
import { CREATE_RFID } from "./mutations";
import { GET_EVENT_ACCESS_TYPES } from "./queries";
import { organizationStore } from "../../store/organization";
import { userStore } from "../../store/user";
import { Event } from "../../types/event";
import toast from "react-hot-toast";

const client = createApolloClient();
const { getState } = organizationStore;
const { getState: getUserStore } = userStore;

interface UploadUIDsModalProps {
  isOpen: boolean;
  onClose: () => void;
  // When provided, the upload is scoped to this event and the in-modal event
  // picker is hidden — pages already scoped to a header-selected event (UID
  // Manager) pass it so bulk uploads can't land on the wrong event.
  eventId?: number | null;
  eventName?: string | null;
}

interface BulkField {
  applyToAll: boolean;
  singleValue: string;
  listValue: string;
}

interface Token {
  name: string;
  balance: number;
}

interface TokenBalance {
  balance: number;
  redeemable_token_id: string;
  redeemable_token_name: string;
}

type TokenBalances = {
  [key: string]: TokenBalance;
};

export default function UploadUIDsModal({
  isOpen,
  onClose,
  eventId = null,
  eventName = null,
}: UploadUIDsModalProps) {
  const initialFocusRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingEvents, setIsFetchingEvents] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [uids, setUids] = useState("");

  // Bulk fields with toggle
  const [printId, setPrintId] = useState<BulkField>({ applyToAll: false, singleValue: "", listValue: "" });
  const [accessType, setAccessType] = useState<BulkField>({ applyToAll: true, singleValue: "", listValue: "" });
  const [cashBalance, setCashBalance] = useState<BulkField>({ applyToAll: true, singleValue: "0", listValue: "" });
  const [promoBalance, setPromoBalance] = useState<BulkField>({ applyToAll: true, singleValue: "0", listValue: "" });

  // Token balances - similar to RfidAssetCreate
  const [tokens, setTokens] = useState<Token[]>([{ name: "", balance: 0 }]);
  const [tokensApplyToAll, setTokensApplyToAll] = useState(true);
  const [tokensListValue, setTokensListValue] = useState("");

  // Upload progress
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; success: number; failed: number } | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [failedUids, setFailedUids] = useState<{ uid: string; error: string }[]>([]);

  useEffect(() => {
    if (isOpen) {
      if (eventId) {
        // Inherited from the calling page — no need to fetch the org's events.
        setSelectedEventId(eventId);
      } else {
        fetchEvents();
      }
    }
  }, [isOpen, eventId]);

  // Access types already on the target event, offered as dropdown suggestions.
  // The field stays free-form — access_type is event-specific text, so the
  // event's own existing values are the only vocabulary worth suggesting.
  const [accessTypeOptions, setAccessTypeOptions] = useState<string[]>([]);
  useEffect(() => {
    if (!isOpen || !selectedEventId) {
      setAccessTypeOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.query<{
          rfid_assets: { access_type: string | null }[];
        }>({
          query: GET_EVENT_ACCESS_TYPES,
          variables: { eventId: selectedEventId },
        });
        if (cancelled) return;
        const names = (data?.rfid_assets ?? [])
          .map((r) => (r.access_type ?? "").trim())
          .filter((s) => s !== "");
        setAccessTypeOptions(Array.from(new Set(names)));
      } catch {
        if (!cancelled) setAccessTypeOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedEventId]);

  const fetchEvents = async () => {
    setIsFetchingEvents(true);
    try {
      const organizationId = getState().organizationId;
      const user = getUserStore().user;

      let where: any = {
        organization_id: { _eq: organizationId },
      };
      if (user?.events) {
        where = {
          ...where,
          id: {
            _in: user?.events,
          },
        };
      }

      const { data } = await client.query({
        query: GET_LIST_EVENTS_BY_ORGANIZATION,
        variables: { where },
      });

      setEvents(data.events);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setIsFetchingEvents(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Reset form
      setSelectedEventId(null);
      setUids("");
      setPrintId({ applyToAll: false, singleValue: "", listValue: "" });
      setAccessType({ applyToAll: true, singleValue: "", listValue: "" });
      setCashBalance({ applyToAll: true, singleValue: "0", listValue: "" });
      setPromoBalance({ applyToAll: true, singleValue: "0", listValue: "" });
      setTokens([{ name: "", balance: 0 }]);
      setTokensApplyToAll(true);
      setTokensListValue("");
      setUploadProgress(null);
      setIsComplete(false);
      setFailedUids([]);
      onClose();
    }
  };

  const getUidList = () => {
    return uids.split("\n").map(line => line.trim()).filter(line => line !== "");
  };

  const getUidCount = () => {
    return getUidList().length;
  };

  // Get duplicate UIDs
  const getDuplicateUids = (): { uid: string; count: number }[] => {
    const uidList = getUidList();
    const counts = new Map<string, number>();
    uidList.forEach(uid => {
      counts.set(uid, (counts.get(uid) || 0) + 1);
    });
    const duplicates: { uid: string; count: number }[] = [];
    counts.forEach((count, uid) => {
      if (count > 1) {
        duplicates.push({ uid, count });
      }
    });
    return duplicates;
  };

  // Validate token JSON entries and return invalid line numbers
  const getInvalidTokenJsonLines = (): number[] => {
    if (tokensApplyToAll) return [];
    const lines = tokensListValue.split("\n").map(v => v.trim());
    const invalidLines: number[] = [];
    lines.forEach((line, index) => {
      if (line === "") return; // Skip empty lines
      try {
        JSON.parse(line);
      } catch {
        invalidLines.push(index + 1); // 1-indexed line numbers
      }
    });
    return invalidLines;
  };

  // Convert tokens array to the proper object format for submission
  const getTokenBalancesObject = (): TokenBalances => {
    const tokenBalances: TokenBalances = {};
    tokens.forEach((token) => {
      if (token.name.trim() !== "") {
        tokenBalances[token.name] = {
          balance: token.balance,
          redeemable_token_id: token.name,
          redeemable_token_name: token.name,
        };
      }
    });
    return tokenBalances;
  };

  // Get the count of items in a bulk field's list
  const getFieldListCount = (field: BulkField): number => {
    if (field.applyToAll) {
      return 0; // Not using list mode
    }
    return field.listValue.split("\n").map(v => v.trim()).filter(v => v !== "").length;
  };

  // Validate that list fields match UID count
  const validateListLengths = (uidCount: number): string[] => {
    const errors: string[] = [];

    const fields = [
      { name: "Print ID", field: printId },
      { name: "Access Type", field: accessType },
      { name: "Cash Balance", field: cashBalance },
      { name: "Promo Balance", field: promoBalance },
    ];

    for (const { name, field } of fields) {
      if (!field.applyToAll) {
        const listCount = getFieldListCount(field);
        if (listCount > 0 && listCount !== uidCount) {
          errors.push(`${name} has ${listCount} values but there are ${uidCount} UIDs`);
        }
      }
    }

    // Validate token balances list if in list mode
    if (!tokensApplyToAll) {
      const tokenListCount = tokensListValue.split("\n").map(v => v.trim()).filter(v => v !== "").length;
      if (tokenListCount > 0 && tokenListCount !== uidCount) {
        errors.push(`Token Balances has ${tokenListCount} values but there are ${uidCount} UIDs`);
      }
    }

    return errors;
  };

  // Get value for a field at a specific index
  const getFieldValue = (field: BulkField, index: number): string => {
    if (field.applyToAll) {
      return field.singleValue;
    }
    const listValues = field.listValue.split("\n").map(v => v.trim());
    return listValues[index] || "";
  };

  // Sanitize balance value - strip out dollar signs, text, and other non-numeric characters
  const sanitizeBalanceValue = (value: string): number => {
    if (!value) return 0;
    // Remove dollar signs, commas, spaces, and any other non-numeric characters except decimal point and minus
    const cleaned = value.replace(/[^0-9.-]/g, "");
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Get token balance for a specific index
  const getTokenBalanceForIndex = (index: number): TokenBalances | object => {
    if (tokensApplyToAll) {
      // Use the shared token configuration
      const tokenBalances = getTokenBalancesObject();
      return Object.keys(tokenBalances).length > 0 ? tokenBalances : {};
    }
    // Use the list value for this index
    const listValues = tokensListValue.split("\n").map(v => v.trim());
    const rawValue = listValues[index] || "";
    if (!rawValue) {
      return {};
    }
    // Try to parse as JSON, otherwise return empty object
    try {
      return JSON.parse(rawValue);
    } catch {
      // If not valid JSON, return empty object
      return {};
    }
  };

  // Token handlers - similar to RfidAssetCreate
  const handleAddToken = () => {
    setTokens([...tokens, { name: "", balance: 0 }]);
  };

  const handleRemoveToken = (index: number) => {
    if (tokens.length > 1) {
      setTokens(tokens.filter((_, i) => i !== index));
    }
  };

  const handleTokenChange = (
    index: number,
    field: keyof Token,
    value: string | number | undefined
  ) => {
    const updatedTokens = [...tokens];
    if (field === "balance") {
      updatedTokens[index].balance = Number(value) || 0;
    } else {
      updatedTokens[index].name = String(value);
    }
    setTokens(updatedTokens);
  };

  const handleUpload = async () => {
    if (!selectedEventId) {
      toast.error("Please select an event");
      return;
    }

    const uidList = getUidList();
    if (uidList.length === 0) {
      toast.error("Please enter at least one UID");
      return;
    }

    // Check for duplicate UIDs
    const duplicates = getDuplicateUids();
    if (duplicates.length > 0) {
      toast.error(`Duplicate UIDs found: ${duplicates.map(d => `${d.uid} (${d.count}x)`).slice(0, 3).join(", ")}${duplicates.length > 3 ? "..." : ""}`);
      return;
    }

    // Validate list lengths match UID count
    const validationErrors = validateListLengths(uidList.length);
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      return;
    }

    // Warn about invalid token JSON (but don't block - they'll default to {})
    const invalidJsonLines = getInvalidTokenJsonLines();
    if (invalidJsonLines.length > 0) {
      toast.error(`Invalid JSON on line(s): ${invalidJsonLines.slice(0, 5).join(", ")}${invalidJsonLines.length > 5 ? "..." : ""} - these will default to empty tokens`);
    }

    const user = getUserStore().user;

    setIsLoading(true);
    setFailedUids([]);
    setUploadProgress({ current: 0, total: uidList.length, success: 0, failed: 0 });

    // Scroll modal to top so user can see progress
    setTimeout(() => {
      panelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);

    let successCount = 0;
    let failedCount = 0;
    const failedList: { uid: string; error: string }[] = [];

    for (let index = 0; index < uidList.length; index++) {
      const uid = uidList[index];

      // Get values for this index
      const regId = getFieldValue(printId, index);
      const accessTypeValue = getFieldValue(accessType, index);
      const cashBalanceValue = getFieldValue(cashBalance, index);
      const promoBalanceValue = getFieldValue(promoBalance, index);

      // Parse balance values (sanitize and convert dollars to cents)
      const cashBalanceCents = Math.round(sanitizeBalanceValue(cashBalanceValue) * 100);
      const promoBalanceCents = Math.round(sanitizeBalanceValue(promoBalanceValue) * 100);

      const input = {
        uid,
        event_id: selectedEventId,
        attendee_id: null,
        // Use null for blank values instead of empty string
        access_type: accessTypeValue.trim() || null,
        reg_id: regId.trim() || null,
        is_active: false,
        cash_balance: cashBalanceCents,
        promo_balance: promoBalanceCents,
        tokens_balance: getTokenBalanceForIndex(index),
        notes: "",
        last_four_phone_numbers: null,
        personnal_pin: null,
        last_updated_by: user?.id,
      };

      try {
        await client.mutate({
          mutation: CREATE_RFID,
          variables: { input },
        });
        successCount++;
      } catch (error: any) {
        failedCount++;
        const message = error?.graphQLErrors?.[0]?.message || "Unknown error";
        failedList.push({ uid, error: message });
        console.error(`Error creating asset ${uid}:`, error);
      }

      setUploadProgress({
        current: index + 1,
        total: uidList.length,
        success: successCount,
        failed: failedCount
      });
    }

    setIsLoading(false);
    setFailedUids(failedList);
    setIsComplete(true);
  };

  // Export failed UIDs to CSV
  const handleExportFailedUids = () => {
    if (failedUids.length === 0) return;

    const csvContent = "UID,Error\n" + failedUids.map(f => `"${f.uid}","${f.error.replace(/"/g, '""')}"`).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `failed_uids_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderBulkField = (
    label: string,
    field: BulkField,
    setField: (value: BulkField) => void,
    placeholder: string,
    type: "text" | "number" = "text",
    suggestions?: string[]
  ) => {
    const listCount = !field.applyToAll ? getFieldListCount(field) : 0;
    const uidTotal = getUidCount();
    const hasMismatch = !field.applyToAll && listCount > 0 && listCount !== uidTotal;
    const suggestionsId =
      suggestions && suggestions.length > 0
        ? `${label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}-suggestions`
        : undefined;

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">{label}</label>
          <label className="flex items-center text-sm text-gray-600 cursor-pointer">
            Apply to all
            <input
              type="checkbox"
              checked={field.applyToAll}
              onChange={(e) => setField({ ...field, applyToAll: e.target.checked })}
              className="ml-2 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
          </label>
        </div>
        {field.applyToAll ? (
          <>
            <input
              type={type}
              list={suggestionsId}
              value={field.singleValue}
              onChange={(e) => setField({ ...field, singleValue: e.target.value })}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder={placeholder}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            {suggestionsId && (
              <datalist id={suggestionsId}>
                {suggestions!.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            )}
            {suggestionsId && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-gray-500">On this event:</span>
                {suggestions!.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setField({ ...field, singleValue: s })}
                    disabled={isLoading}
                    className={`rounded-full border px-2 py-0.5 text-xs transition-colors ${
                      field.singleValue === s
                        ? "border-red-700 bg-red-700 text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">One value per line</span>
              {listCount > 0 && (
                <span className={`text-sm ${hasMismatch ? "text-red-600 font-medium" : "text-gray-500"}`}>
                  {listCount.toLocaleString()} value{listCount !== 1 ? "s" : ""}
                  {hasMismatch && ` (expected ${uidTotal})`}
                </span>
              )}
            </div>
            <textarea
              value={field.listValue}
              onChange={(e) => setField({ ...field, listValue: e.target.value })}
              placeholder={`Paste list of ${label.toLowerCase()} values (one per line, matching UIDs order)`}
              rows={4}
              className={`block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono ${hasMismatch ? "border-red-300" : "border-gray-300"}`}
            />
          </div>
        )}
      </div>
    );
  };

  const uidCount = getUidCount();
  const canUpload = selectedEventId && uidCount > 0 && !isLoading;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[1000]"
        initialFocus={initialFocusRef}
        onClose={isLoading ? () => {} : handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 z-[1000]"
            aria-hidden="true"
            style={{ backgroundColor: "rgba(0,0,0,0.2)" }}
          />
        </Transition.Child>

        <div className="fixed inset-0 z-[1001] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel ref={panelRef} className="relative z-[1002] transform overflow-hidden rounded-lg bg-white px-6 py-8 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                <div ref={initialFocusRef} tabIndex={-1} aria-hidden="true" />

                {isComplete ? (
                  /* Completion View */
                  <div className="space-y-6">
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-semibold leading-6 text-gray-900"
                    >
                      Upload Complete
                    </Dialog.Title>

                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-green-700">
                          {uploadProgress?.success.toLocaleString() || 0}
                        </p>
                        <p className="text-sm text-green-600">Successfully Created</p>
                      </div>
                      <div className={`${failedUids.length > 0 ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"} border rounded-lg p-4 text-center`}>
                        <p className={`text-3xl font-bold ${failedUids.length > 0 ? "text-red-700" : "text-gray-400"}`}>
                          {failedUids.length.toLocaleString()}
                        </p>
                        <p className={`text-sm ${failedUids.length > 0 ? "text-red-600" : "text-gray-500"}`}>Failed</p>
                      </div>
                    </div>

                    {/* Failed UIDs List */}
                    {failedUids.length > 0 && (
                      <div className="border border-red-200 rounded-lg overflow-hidden">
                        <div className="bg-red-50 px-4 py-3 flex items-center justify-between">
                          <h4 className="text-sm font-medium text-red-800">Failed UIDs</h4>
                          <SecondaryButton onClick={handleExportFailedUids}>
                            Export Failed UIDs
                          </SecondaryButton>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <table className="min-w-full divide-y divide-red-200">
                            <thead className="bg-red-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">UID</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Error</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-red-100">
                              {failedUids.map((item, idx) => (
                                <tr key={idx}>
                                  <td className="px-4 py-2 text-sm font-mono text-gray-900">{item.uid}</td>
                                  <td className="px-4 py-2 text-sm text-red-600">{item.error}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Success Message */}
                    {failedUids.length === 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                        <p className="text-green-800">All assets were created successfully!</p>
                      </div>
                    )}

                    <div className="flex justify-end pt-4 border-t border-gray-200">
                      <PrimaryButton onClick={handleClose}>
                        Close
                      </PrimaryButton>
                    </div>
                  </div>
                ) : (
                  /* Upload Form */
                  <>
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-semibold leading-6 text-gray-900 mb-6"
                    >
                      Bulk Upload Cashless Assets
                    </Dialog.Title>

                    {isFetchingEvents ? (
                      <div className="text-center py-8">
                        <p className="text-gray-600">Loading events...</p>
                      </div>
                ) : (
                  <div className="space-y-6">
                    {/* Progress Bar */}
                    {uploadProgress && (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                        <div className="flex justify-between text-sm text-blue-900 mb-2">
                          <span>Processing: {uploadProgress.current} / {uploadProgress.total}</span>
                          <span className="text-green-600">{uploadProgress.success} succeeded</span>
                          {uploadProgress.failed > 0 && (
                            <span className="text-red-600">{uploadProgress.failed} failed</span>
                          )}
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Event Selector — hidden when the event is inherited */}
                    {eventId ? (
                      <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Uploading to
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {eventName ?? `Event #${eventId}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          Inherited from the event selected in the header.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <CustomSelect
                          title="Event"
                          name="event_id"
                          required
                          onChange={(value) => setSelectedEventId(value as number)}
                          options={events.map((event) => ({
                            label: event.name,
                            value: event.id,
                          }))}
                        />
                      </div>
                    )}

                    {/* UIDs Input */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          UIDs <span className="text-red-500">*</span>
                        </label>
                        {uidCount > 0 && (
                          <span className="text-sm text-gray-500">
                            {uidCount.toLocaleString()} UID{uidCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      <textarea
                        value={uids}
                        onChange={(e) => setUids(e.target.value)}
                        placeholder="Paste UIDs here (one per line)"
                        rows={6}
                        disabled={isLoading}
                        className={`block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono disabled:bg-gray-100 ${getDuplicateUids().length > 0 ? "border-red-300" : "border-gray-300"}`}
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Paste a list of UIDs, one per line. Supports up to 5000+ UIDs.
                      </p>
                      {/* Inline duplicate warning */}
                      {getDuplicateUids().length > 0 && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-sm text-red-700 font-medium">
                            Duplicate UIDs detected:
                          </p>
                          <ul className="mt-1 text-sm text-red-600 list-disc list-inside">
                            {getDuplicateUids().slice(0, 5).map((dup, idx) => (
                              <li key={idx}>
                                <code className="bg-red-100 px-1 rounded">{dup.uid}</code> appears {dup.count} times
                              </li>
                            ))}
                            {getDuplicateUids().length > 5 && (
                              <li>...and {getDuplicateUids().length - 5} more duplicates</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>

                    <hr className="border-gray-200" />

                    {/* Bulk Fields */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-gray-700">
                        Asset Properties
                      </h4>
                      <p className="text-sm text-gray-500">
                        For each field below, you can either apply a single value to all UIDs,
                        or paste a list of values (one per line) matching the order of UIDs above.
                      </p>

                      {renderBulkField("Print ID", printId, setPrintId, "Enter Print ID")}
                      {renderBulkField(
                        "Access Type",
                        accessType,
                        setAccessType,
                        accessTypeOptions.length > 0
                          ? "Select or type an access type"
                          : "Enter Access Type",
                        "text",
                        accessTypeOptions
                      )}
                      {renderBulkField("Cash Balance ($)", cashBalance, setCashBalance, "0.00", "number")}
                      {renderBulkField("Promo Balance ($)", promoBalance, setPromoBalance, "0.00", "number")}

                      {/* Token Balances - similar to RfidAssetCreate */}
                      <div className="border border-gray-200 rounded-md p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <p className="block text-sm font-medium text-gray-700">
                            Token Balances (Optional)
                          </p>
                          <label className="flex items-center text-sm text-gray-600 cursor-pointer">
                            Apply to all
                            <input
                              type="checkbox"
                              checked={tokensApplyToAll}
                              onChange={(e) => setTokensApplyToAll(e.target.checked)}
                              className="ml-2 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                          </label>
                        </div>

                        {tokensApplyToAll ? (
                          <>
                            <p className="text-sm text-gray-500">
                              Token balances will be applied to all uploaded assets.
                            </p>
                            <div className="space-y-3">
                              {tokens.map((token, index) => (
                                <div key={index} className="grid grid-cols-12 gap-4">
                                  <div className="col-span-6">
                                    <Input
                                      label="Token Name"
                                      name={`tokens[${index}][name]`}
                                      value={token.name}
                                      onChange={(value) => handleTokenChange(index, "name", String(value))}
                                    />
                                  </div>
                                  <div className="col-span-4">
                                    <Input
                                      label="Balance"
                                      name={`tokens[${index}][balance]`}
                                      value={token.balance}
                                      type="number"
                                      onChange={(value) => handleTokenChange(index, "balance", Number(value) || 0)}
                                    />
                                  </div>
                                  <div className="col-span-2 flex items-end">
                                    {tokens.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveToken(index)}
                                        disabled={isLoading}
                                        className="mb-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 disabled:opacity-50"
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                              <div className="mt-2">
                                <PrimaryButton type="button" onClick={handleAddToken} isDisabled={isLoading}>
                                  + Add Token
                                </PrimaryButton>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-gray-500">
                              Paste a list of token balance JSON values (one per line, matching UIDs order).
                              Use <code className="bg-gray-100 px-1 rounded">{"{}"}</code> for no tokens.
                            </p>
                            {(() => {
                              const tokenListCount = tokensListValue.split("\n").map(v => v.trim()).filter(v => v !== "").length;
                              const hasTokenMismatch = tokenListCount > 0 && tokenListCount !== uidCount;
                              const invalidJsonLines = getInvalidTokenJsonLines();
                              const hasInvalidJson = invalidJsonLines.length > 0;
                              return (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-gray-500">One JSON value per line</span>
                                    {tokenListCount > 0 && (
                                      <span className={`text-sm ${hasTokenMismatch ? "text-red-600 font-medium" : "text-gray-500"}`}>
                                        {tokenListCount.toLocaleString()} value{tokenListCount !== 1 ? "s" : ""}
                                        {hasTokenMismatch && ` (expected ${uidCount})`}
                                      </span>
                                    )}
                                  </div>
                                  <textarea
                                    value={tokensListValue}
                                    onChange={(e) => setTokensListValue(e.target.value)}
                                    placeholder={'{"tokenName": {"balance": 10, "redeemable_token_id": "tokenName", "redeemable_token_name": "tokenName"}}\n{}'}
                                    rows={4}
                                    disabled={isLoading}
                                    className={`block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono disabled:bg-gray-100 ${hasTokenMismatch || hasInvalidJson ? "border-red-300" : "border-gray-300"}`}
                                  />
                                  {/* Inline invalid JSON warning */}
                                  {hasInvalidJson && (
                                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                      <p className="text-sm text-yellow-700 font-medium">
                                        Invalid JSON on line{invalidJsonLines.length !== 1 ? "s" : ""}:
                                      </p>
                                      <p className="mt-1 text-sm text-yellow-600">
                                        {invalidJsonLines.slice(0, 10).join(", ")}
                                        {invalidJsonLines.length > 10 && ` ...and ${invalidJsonLines.length - 10} more`}
                                      </p>
                                      <p className="mt-1 text-xs text-yellow-600">
                                        These lines will default to empty token balances ({"{}"}).
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-2 pt-4 border-t border-gray-200">
                      <SecondaryButton onClick={handleClose} isDisabled={isLoading}>
                        {isLoading ? "Processing..." : "Cancel"}
                      </SecondaryButton>
                      <PrimaryButton
                        onClick={handleUpload}
                        isLoading={isLoading}
                        isDisabled={!canUpload}
                      >
                        Upload {uidCount > 0 ? `(${uidCount.toLocaleString()})` : ""}
                      </PrimaryButton>
                    </div>
                  </div>
                    )}
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
